import Link, { type LinkProps } from "@/infrastructure/router/Link";
import { useTranslation } from "@/i18n/ArtistI18n";
import type { ReactNode } from "react";
import { ArtistIcon } from "../icons/ArtistIcon";

export interface NavigationBarProps {
	readonly title: ReactNode;
	readonly backHref?: LinkProps<string>["href"];
	readonly backLabel?: string;
	readonly leading?: ReactNode;
	readonly trailing?: ReactNode;
	readonly className?: string;
}

/**
 * NavigationBar — sticky top bar with a leading back control (or custom
 * leading slot), a centered title, and a trailing slot. Mirrors
 * `DSGlassNavigationBar` on iOS.
 */
export function NavigationBar({
	title,
	backHref,
	backLabel,
	leading,
	trailing,
	className = "",
}: Readonly<NavigationBarProps>) {
	const { t } = useTranslation();

	return (
		<header className={`ds-navigation-bar ${className}`.trim()}>
			<div className="ds-navigation-bar__leading">
				{leading ??
					(backHref === undefined ? null : (
						<Link
							className="ds-icon-button"
							href={backHref}
							aria-label={backLabel ?? t("common.back")}
						>
							<ArtistIcon name="back" size={30} strokeWidth={2.2} />
						</Link>
					))}
			</div>
			<h1>{title}</h1>
			<div className="ds-navigation-bar__trailing">{trailing}</div>
		</header>
	);
}
